import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, FileText, X } from 'lucide-react';
import { getDownloadURL, ref } from 'firebase/storage';
import type { RfqPackage, RfqProject, RfqSubmission } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { storage } from '../../utils/firebase';
import {
  canCommitCombinedQuoteSplit,
  combinedQuoteSplitMissingCncCapability,
  commitCombinedQuoteSplit,
  resolveSplitBoardFeetPrefill,
  suggestCncAmountFromRate,
  timberAmountFromCnc,
  validateCombinedQuoteSplit,
} from '../../utils/combinedQuoteSplit';
import { selectRfqProjectForDeal } from '../../utils/vendorAdminProjectSummary';
import { formatQuoteTotal, parseQuoteTotalInput, submissionQuoteTotal } from '../../utils/rfqQuoteTotal';
import { canApproveSupplierQuoteOnBehalf } from '../../utils/staffSupplierQuoteAccess';
import './LegacyQuoteEditModal.css';
import './CombinedQuoteSplitModal.css';

/** Stable defaults so a missing prop does not look like new data on every render. */
const EMPTY_RFQ_PACKAGES: RfqPackage[] = [];
const EMPTY_RFQ_SUBMISSIONS: RfqSubmission[] = [];

export type CombinedQuoteSplitModalProps = {
  open: boolean;
  onClose: () => void;
  timberSubmission: RfqSubmission | null;
  timberProject: RfqProject;
  /** Null when the deal has no cnc_quote RFQ yet — created on commit. */
  cncProject: RfqProject | null;
  orgId: string;
  orgName: string;
  packages: RfqPackage[];
  submissionsByPackageId: Map<string, RfqSubmission[]>;
  selectedTimberReleaseId: string | null;
  cncPackagesForOrg?: RfqPackage[];
  /** CNC submissions for this org (idempotent retry). */
  cncSubmissionsForOrg?: RfqSubmission[];
  hubProjectManagerUid?: string;
  onSuccess?: () => void;
};

const CombinedQuoteSplitModal: React.FC<CombinedQuoteSplitModalProps> = ({
  open,
  onClose,
  timberSubmission,
  timberProject,
  cncProject,
  orgId,
  orgName,
  packages,
  submissionsByPackageId,
  selectedTimberReleaseId,
  cncPackagesForOrg = EMPTY_RFQ_PACKAGES,
  cncSubmissionsForOrg = EMPTY_RFQ_SUBMISSIONS,
  hubProjectManagerUid,
  onSuccess,
}) => {
  const { user } = useAuth();
  const {
    crmUsers,
    supplierOrgs,
    rfqProjects,
    publishRfqPackage,
    createRfqSubmission,
    updateRfqSubmission,
    updateRfqProject,
    createRfqProject,
  } = useCRM();
  const [boardFeet, setBoardFeet] = useState('');
  const [ratePerBdft, setRatePerBdft] = useState('');
  const [cncAmount, setCncAmount] = useState('');
  const [timberAmount, setTimberAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotePreviewUrl, setQuotePreviewUrl] = useState<string | null>(null);
  const [quotePreviewError, setQuotePreviewError] = useState<string | null>(null);
  const [quotePreviewLoading, setQuotePreviewLoading] = useState(false);
  /** Submission id we already seeded. Re-seeding on array identity wiped in-progress amounts. */
  const seededSubmissionIdRef = useRef<string | null>(null);

  const canCommit = useMemo(() => {
    const row = crmUsers.find((u) => u.uid === user?.id);
    return canApproveSupplierQuoteOnBehalf({
      authRole: user?.role,
      designations: row?.designations,
      hubProjectManagerUid,
      userId: user?.id,
    });
  }, [user, crmUsers, hubProjectManagerUid]);

  const combinedTotal = submissionQuoteTotal(timberSubmission) ?? 0;
  const supplierOrg = useMemo(
    () => supplierOrgs.find((o) => o.id === orgId) ?? null,
    [supplierOrgs, orgId],
  );
  const missingCncCapability = combinedQuoteSplitMissingCncCapability(supplierOrg);
  const willCreateCncProject = !cncProject?.id;
  const quoteStoragePath = (timberSubmission?.storagePath || '').trim();
  const quoteFileName = timberSubmission?.fileName?.trim() || 'Quote PDF';

  useEffect(() => {
    if (!open) {
      seededSubmissionIdRef.current = null;
      return;
    }
    const submissionId = timberSubmission?.id;
    if (!submissionId || seededSubmissionIdRef.current === submissionId) return;
    seededSubmissionIdRef.current = submissionId;
    const bf = resolveSplitBoardFeetPrefill({ cncProject, cncPackagesForOrg });
    setBoardFeet(bf > 0 ? String(bf) : '');
    setRatePerBdft('');
    setCncAmount('');
    setTimberAmount(combinedTotal > 0 ? String(combinedTotal) : '');
    setError(null);
  }, [open, timberSubmission?.id, cncProject, cncPackagesForOrg, combinedTotal]);

  useEffect(() => {
    if (!open || !quoteStoragePath) {
      setQuotePreviewUrl(null);
      setQuotePreviewError(null);
      setQuotePreviewLoading(false);
      return;
    }
    let cancelled = false;
    setQuotePreviewUrl(null);
    setQuotePreviewError(null);
    setQuotePreviewLoading(true);
    getDownloadURL(ref(storage, quoteStoragePath))
      .then((url) => {
        if (!cancelled) setQuotePreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQuotePreviewError('Could not load the quote PDF.');
      })
      .finally(() => {
        if (!cancelled) setQuotePreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, quoteStoragePath]);

  if (!open || !timberSubmission?.id) return null;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const applyCncAmount = (nextCnc: number | null) => {
    if (nextCnc == null) {
      setCncAmount('');
      setTimberAmount(combinedTotal > 0 ? String(combinedTotal) : '');
      return;
    }
    setCncAmount(String(nextCnc));
    setTimberAmount(String(timberAmountFromCnc(combinedTotal, nextCnc)));
  };

  const handleRateChange = (raw: string) => {
    setRatePerBdft(raw);
    const rate = parseFloat(raw.trim());
    const bf = parseFloat(boardFeet.trim());
    if (Number.isFinite(rate) && rate > 0 && Number.isFinite(bf) && bf > 0) {
      applyCncAmount(suggestCncAmountFromRate(rate, bf));
    }
  };

  const handleBoardFeetChange = (raw: string) => {
    setBoardFeet(raw);
    const rate = parseFloat(ratePerBdft.trim());
    const bf = parseFloat(raw.trim());
    if (Number.isFinite(rate) && rate > 0 && Number.isFinite(bf) && bf > 0) {
      applyCncAmount(suggestCncAmountFromRate(rate, bf));
    }
  };

  const handleCncAmountChange = (raw: string) => {
    setCncAmount(raw);
    const parsed = parseQuoteTotalInput(raw);
    if (parsed != null) {
      setTimberAmount(String(timberAmountFromCnc(combinedTotal, parsed)));
    }
  };

  const handleTimberAmountChange = (raw: string) => {
    setTimberAmount(raw);
    const parsed = parseQuoteTotalInput(raw);
    if (parsed != null) {
      setCncAmount(String(timberAmountFromCnc(combinedTotal, parsed)));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      setError('You must be signed in.');
      return;
    }
    if (!canCommit) {
      setError('Only approvers can split a combined quote.');
      return;
    }
    const gate = canCommitCombinedQuoteSplit({
      timberProject,
      timberSubmission,
      packages,
      submissionsByPackageId,
      selectedReleaseId: selectedTimberReleaseId,
    });
    if (!gate.ok) {
      setError(gate.error);
      return;
    }

    const cnc = parseQuoteTotalInput(cncAmount);
    const timber = parseQuoteTotalInput(timberAmount);
    if (cnc == null || timber == null) {
      setError('Enter valid timber and CNC amounts.');
      return;
    }
    const validated = validateCombinedQuoteSplit({
      combinedTotal,
      cncAmount: cnc,
      timberAmount: timber,
    });
    if (!validated.ok) {
      setError(validated.error);
      return;
    }

    const bf = parseFloat(boardFeet.trim());
    const rate = ratePerBdft.trim() ? parseFloat(ratePerBdft.trim()) : null;

    setBusy(true);
    setError(null);
    try {
      await commitCombinedQuoteSplit(
        {
          publishRfqPackage,
          createRfqSubmission,
          updateRfqSubmission,
          updateRfqProject,
          createRfqProject,
          findCncProjectForDeal: (dealId) => selectRfqProjectForDeal(dealId, 'cnc_quote', rfqProjects),
          existingCncSubmissionsForOrg: cncSubmissionsForOrg,
        },
        {
          timberProject,
          cncProject,
          timberSubmission,
          packages,
          orgId,
          orgName,
          combinedTotal: validated.combinedTotal,
          timberAmount: validated.timberAmount,
          cncAmount: validated.cncAmount,
          boardFeet: Number.isFinite(bf) && bf > 0 ? bf : 0,
          ratePerBdft: rate != null && Number.isFinite(rate) && rate > 0 ? rate : null,
          staffUserId: user.id,
          staffUserName: user.name || user.email || 'Staff',
        },
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not split quote.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lqi-modal-backdrop" role="presentation" onClick={handleClose}>
      <div
        className={`lqi-modal lqi-modal--timber cqs-modal${quoteStoragePath ? ' cqs-modal--with-preview' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="combined-quote-split-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lqi-modal-head">
          <div>
            <h2 id="combined-quote-split-title">Split CNC from quote</h2>
            <p className="lqi-modal-sub">
              Carve CNC dollars out of {orgName}&apos;s combined quote so timber and CNC can be
              approved and billed separately. Creates a CNC version row labeled &ldquo;From quote, no
              3D model provided&rdquo; until a real model is staged.
            </p>
          </div>
          <button type="button" className="lqi-modal-close" onClick={handleClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className={quoteStoragePath ? 'cqs-body' : undefined}>
          {quoteStoragePath ? (
            <aside className="cqs-preview" aria-label="Quote PDF preview">
              <div className="cqs-preview-head">
                <span className="cqs-preview-title">
                  <FileText size={14} aria-hidden />
                  {quoteFileName}
                </span>
                {quotePreviewUrl ? (
                  <a
                    className="cqs-preview-open"
                    href={quotePreviewUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink size={13} aria-hidden />
                    Open
                  </a>
                ) : null}
              </div>
              {quotePreviewLoading && !quotePreviewUrl ? (
                <p className="cqs-preview-status">Loading quote…</p>
              ) : null}
              {quotePreviewError && !quotePreviewLoading ? (
                <p className="cqs-preview-status">{quotePreviewError}</p>
              ) : null}
              {quotePreviewUrl ? (
                <iframe
                  className="cqs-preview-frame"
                  title={`Quote PDF for ${orgName}`}
                  src={quotePreviewUrl}
                />
              ) : null}
            </aside>
          ) : null}
        <form className="lqi-modal-body" onSubmit={(ev) => void handleSubmit(ev)}>
          <label className="lqi-field">
            <span>Combined total (locked)</span>
            <input type="text" value={formatQuoteTotal(combinedTotal)} disabled readOnly />
          </label>
          <div className="lqi-species-quote-line">
            <label className="lqi-field lqi-field--inline">
              <span>Board footage</span>
              <input
                type="text"
                inputMode="decimal"
                value={boardFeet}
                disabled={busy}
                onChange={(e) => handleBoardFeetChange(e.target.value)}
              />
            </label>
            <label className="lqi-field lqi-field--inline">
              <span>CNC rate $/BF (optional)</span>
              <input
                type="text"
                inputMode="decimal"
                value={ratePerBdft}
                disabled={busy}
                onChange={(e) => handleRateChange(e.target.value)}
              />
            </label>
          </div>
          <div className="lqi-species-quote-line">
            <label className="lqi-field lqi-field--inline">
              <span>CNC amount ($)</span>
              <input
                type="text"
                inputMode="decimal"
                value={cncAmount}
                disabled={busy}
                required
                onChange={(e) => handleCncAmountChange(e.target.value)}
              />
            </label>
            <label className="lqi-field lqi-field--inline">
              <span>Timber amount ($)</span>
              <input
                type="text"
                inputMode="decimal"
                value={timberAmount}
                disabled={busy}
                required
                onChange={(e) => handleTimberAmountChange(e.target.value)}
              />
            </label>
          </div>
          <p className="cqs-hint">
            Timber + CNC must equal the combined total. Freight embedded in the PDF stays in the
            timber slice by default.
          </p>
          {willCreateCncProject && (
            <p className="cqs-hint">
              No CNC work order exists yet for this deal — splitting will create one and add{' '}
              {orgName} to its supplier roster.
            </p>
          )}
          {missingCncCapability && (
            <p className="cqs-hint">
              {orgName} is not flagged with CNC capability yet. You can still split; they will be
              added to the CNC roster for this deal.
            </p>
          )}
          {!canCommit && (
            <p className="lqi-error">Only approvers can commit a split (same gate as quote approve).</p>
          )}
          {error && <p className="lqi-error">{error}</p>}
          <div className="lqi-modal-actions">
            <button type="button" className="lqi-btn lqi-btn--ghost" disabled={busy} onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="lqi-btn lqi-btn--primary" disabled={busy || !canCommit}>
              {busy ? 'Splitting…' : 'Split CNC from quote'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default CombinedQuoteSplitModal;
